import { useEffect, type ReactNode } from "react";

/** Slide-over drawer from the right. Closes on backdrop click or Escape. */
export function Drawer({
  open,
  onClose,
  title,
  viewFullHref,
  width = 480,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  viewFullHref?: string;
  width?: number;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55]">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={title}
        className="drawer-panel absolute top-0 right-0 h-full bg-ink-800/95 border-l border-white/10 shadow-2xl overflow-auto"
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 bg-ink-800/95 backdrop-blur">
          <h2 className="font-semibold text-slate-100 truncate">{title}</h2>
          <div className="flex items-center gap-2">
            {viewFullHref && (
              <a
                href={viewFullHref}
                className="text-xs text-accent-500 hover:underline"
              >
                Open full page →
              </a>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-100 px-2"
              aria-label="Close drawer"
            >
              ✕
            </button>
          </div>
        </header>
        <div className="p-5">{children}</div>
      </aside>
    </div>
  );
}
